<template lang="pug">
  #AdminSuperProgramsTable
    BNotification(:active.sync="showError" type="is-danger" :duration="4000")
      | {{ errorMessage }}
    BNotification(:active.sync="showSuccess" type="is-success" :duration="3000")
      | {{ successMessage }}

    .box.bordered
      //- Filters
      .columns
        .column.is-narrow
          BField(label="Status")
            BRadioButton(
              v-model="filterActive"
              native-value="all"
            ) All
            BRadioButton(
              v-model="filterActive"
              native-value="active"
            ) Active
            BRadioButton(
              v-model="filterActive"
              native-value="inactive"
            ) Inactive

        .column.is-narrow
          BField(label="Reseller")
            BSelect(
              v-model="filterResellerId"
              placeholder="All Resellers"
            )
              option(:value="null") -- All Resellers --
              option(
                v-for="reseller in resellers"
                :key="reseller.id"
                :value="reseller.id"
              ) {{ reseller.name }}

        .column
          BField(label="Search")
            BInput(
              v-model="searchQuery"
              type="search"
              placeholder="Search by ID, name, or URL"
            )

      //- Table
      .table-container
        table.table.is-fullwidth.is-striped
          thead
            tr
              th ID
              th Name
              th URL
              th Active
              th Reseller
              th(style="width: 50px;") Actions
          tbody
            template(v-for="program in programs")
              tr(:key="program.id")
                td {{ program.id }}
                td {{ program.name }}
                td
                  a(:href="program.url" target="_blank") {{ program.url }}
                td
                  BTag(
                    :type="program.active ? 'is-success' : 'is-danger'"
                  ) {{ program.active ? 'Active' : 'Inactive' }}
                td {{ program.resellerName || '—' }}
                td
                  WtButton(
                    is-text
                    bold
                    @click.native="openProgramModal(program)"
                  ) INSPECT

      //- Pagination
      .mt-5.has-text-centered(v-if="pagination.total > 0")
        BPagination(
          :total="pagination.total"
          :per-page="pagination.perPage"
          :current.sync="currentPage"
          @change="onPageChange"
        )

    BModal(
      has-modal-card
      :active.sync="programModalOpen"
    )
      ProgramStatsModal(
        :program="selectedProgram"
        @close="onCloseProgramModal"
      )
</template>

<script>
import WtButton from "@@/WtButton/WtButton";
import ProgramStatsModal from "@@/StatsReportingAdminSuper/ProgramStatsModal";

export default {
  name: "AdminSuperProgramsTable",

  components: {
    WtButton,
    ProgramStatsModal
  },

  data() {
    return {
      programs: [],
      pagination: { total: 0, perPage: 15, currentPage: 1 },
      currentPage: 1,
      filterActive: "all",
      filterResellerId: null,
      searchQuery: "",
      resellers: [],
      expandedProgramId: null,
      loading: false,
      showError: false,
      showSuccess: false,
      errorMessage: "",
      successMessage: "",
      searchTimeout: null,
      programModalOpen: false,
      selectedProgram: {}
    };
  },

  watch: {
    filterActive() {
      this.currentPage = 1;
      this.fetchPrograms();
    },

    filterResellerId() {
      this.currentPage = 1;
      this.fetchPrograms();
    },

    searchQuery() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchPrograms();
      }, 500);
    }
  },

  mounted() {
    this.fetchResellers();
    this.fetchPrograms();
    this.$root.$on(
      "admin-super-programs:request-zero-out",
      this.handleGlobalZeroOut
    );
  },

  beforeDestroy() {
    this.$root.$off(
      "admin-super-programs:request-zero-out",
      this.handleGlobalZeroOut
    );
  },

  methods: {
    async fetchPrograms() {
      this.loading = true;
      try {
        const params = {
          perPage: this.pagination.perPage,
          page: this.currentPage
        };
        if (this.filterActive !== "all") {
          params.active = this.filterActive === "active" ? 1 : 0;
        }
        if (this.filterResellerId) {
          params.resellerId = this.filterResellerId;
        }
        if (this.searchQuery.trim()) {
          const response = await this.axios.get("programs/search", {
            params: {
              searchString: this.searchQuery,
              perPage: this.pagination.perPage,
              page: this.currentPage
            }
          });
          this.programs = response.data.data || [];
          this.pagination = response.data.pagination || {};
        } else {
          const response = await this.axios.get("programs", { params });
          this.programs = response.data.data || [];
          this.pagination = response.data.pagination || {};
        }
        this.expandedProgramId = null;
      } catch (error) {
        console.error("Error fetching programs:", error);
        this.showErrorMessage("Failed to load programs");
      } finally {
        this.loading = false;
      }
    },

    async fetchResellers() {
      try {
        const response = await this.axios.get("super/reseller/getAllResellers");
        this.resellers = response.data.data.items || [];
      } catch (error) {
        console.error("Error fetching resellers:", error);
      }
    },

    onPageChange() {
      this.fetchPrograms();
    },

    toggleExpand(programId) {
      this.expandedProgramId =
        this.expandedProgramId === programId ? null : programId;
    },

    toggleSelectProgram(programId, selected) {
      if (selected) {
        this.selectedPrograms.add(programId);
      } else {
        this.selectedPrograms.delete(programId);
      }
    },

    toggleSelectAll(selected) {
      this.selectedPrograms.clear();
      if (selected) {
        this.programs.forEach(p => this.selectedPrograms.add(p.id));
      }
    },

    async bulkSetActive(active) {
      this.bulkUpdating = true;
      let succeeded = 0;
      let failed = 0;

      for (const programId of this.selectedPrograms) {
        try {
          await this.axios.put(`super/programs/${programId}`, { active });
          succeeded++;
        } catch (error) {
          console.error("Error updating program", error);
          failed++;
        }
      }

      this.bulkUpdating = false;
      if (failed === 0) {
        this.showSuccessMessage(`Updated ${succeeded} program(s)`);
      } else {
        this.showErrorMessage(`Updated ${succeeded}, failed ${failed}`);
      }
      this.fetchPrograms();
    },

    async handleGlobalZeroOut() {
      const programId = this.expandedProgramId;
      if (!programId) {
        this.showErrorMessage("No program selected");
        return;
      }

      try {
        await this.axios.post(`super/programs/${programId}/zero-out`);
        this.showSuccessMessage(
          "Zero-out scheduled. Data will be deleted soon."
        );
        this.fetchPrograms();
      } catch (error) {
        console.error("Error scheduling zero-out:", error);
        const message =
          error.response?.data?.message || "Failed to schedule zero-out";
        this.showErrorMessage(message);
      }
    },

    onProgramUpdated(program) {
      const index = this.programs.findIndex(p => p.id === program.id);
      if (index !== -1) {
        this.programs.splice(index, 1, program);
      }
      this.showSuccessMessage("Program updated");
    },

    onAdminAdded() {
      this.showSuccessMessage("Admin added");
      this.refreshExpandedProgram();
    },

    onAdminRemoved() {
      this.showSuccessMessage("Admin removed");
      this.refreshExpandedProgram();
    },

    onZeroOut() {
      this.$root.$emit("admin-super-programs:request-zero-out");
    },

    refreshExpandedProgram() {
      if (this.expandedProgramId) {
        const programId = this.expandedProgramId;
        this.$root.$emit("expanded-program-row:refresh", programId);
      }
    },

    showErrorMessage(message) {
      this.errorMessage = message;
      this.showError = true;
    },

    showSuccessMessage(message) {
      this.successMessage = message;
      this.showSuccess = true;
    },

    openProgramModal(program) {
      this.selectedProgram = program;
      this.programModalOpen = true;
    },

    onCloseProgramModal() {
      this.programModalOpen = false;
      this.selectedProgram = {};
      this.fetchPrograms();
    }
  }
};
</script>

<style lang="sass" scoped>
#AdminSuperProgramsTable
  .table-container
    overflow-x: auto
    overflow-y: visible

  table
    margin-top: 1rem

  td
    overflow: visible !important
    vertical-align: middle

  .box
    margin-bottom: 2rem

  /deep/ .button.is-text
    background-color: transparent
    border: 1px solid transparent
    transition: all 0.2s ease

    &:hover
      border-color: #b5b5b5
      background-color: transparent

  /deep/ .modal-close
    visibility: hidden
</style>
